import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Zap, Eye, EyeOff, ArrowRight, Loader2, ShieldCheck, PartyPopper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Retry transient Cloudflare / edge errors so a stray 520 doesn't force the
// customer to re-enter their data. Same logic as in AuthContext.
const TRANSIENT_STATUS = new Set([502, 503, 504, 520, 521, 522, 523, 524, 408]);
async function _postRetry(url, body, config = {}, attempts = 5, baseDelayMs = 800) {
  let lastErr;
  for (let i = 1; i <= attempts; i++) {
    try { return await axios.post(url, body, config); }
    catch (err) {
      lastErr = err;
      const transient = !err.response || TRANSIENT_STATUS.has(err.response.status);
      if (!transient || i === attempts) throw err;
      await new Promise(r => setTimeout(r, baseDelayMs * i));
    }
  }
  throw lastErr;
}

function formatError(detail) {
  if (!detail) return "Etwas ist schiefgelaufen";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const purchased = searchParams.get("purchased") === "true";

  const [mode, setMode] = useState(purchased ? "purchased" : "login");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // Purchase activation flow
  const [purchaseStep, setPurchaseStep] = useState("email"); // email -> code -> done
  const [activationCode, setActivationCode] = useState("");
  const [purchasePlan, setPurchasePlan] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        navigate("/dashboard");
      } else if (mode === "reset") {
        await _postRetry(`${API}/auth/reset-password`, { email, recovery_code: resetCode, new_password: newPassword });
        toast.success("Passwort zurueckgesetzt!");
        setMode("login");
      } else if (mode === "purchased" && purchaseStep === "email") {
        // Lookup purchase by email
        const { data } = await _postRetry(`${API}/auth/lookup-purchase`, { email });
        setActivationCode(data.activation_code);
        setPurchasePlan(data.plan);
        setPurchaseStep("code");
      } else if (mode === "purchased" && purchaseStep === "code") {
        // Activate account with the shown code + user's password
        const { data } = await _postRetry(`${API}/auth/activate`, { email, activation_code: activationCode, password }, { withCredentials: true });
        setRecoveryCode(data.recovery_code);
        toast.success("Account aktiviert!");
      } else if (mode === "activate") {
        const { data } = await _postRetry(`${API}/auth/activate`, { email, activation_code: activationCode, password }, { withCredentials: true });
        setRecoveryCode(data.recovery_code);
        toast.success("Account aktiviert!");
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Recovery code screen (after register or activate)
  if (recoveryCode) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-start sm:items-center justify-center px-4 pt-10 sm:pt-0 pb-40 sm:pb-10">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Dein Recovery-Code</h1>
            <p className="text-sm text-zinc-400">Speichere diesen Code sicher ab. Damit kannst du dein Passwort zuruecksetzen.</p>
          </div>
          <div className="p-6 rounded-xl bg-zinc-900 border border-zinc-800 text-center">
            <p className="text-3xl font-mono font-bold tracking-widest" style={{ color: '#00D4FF' }} data-testid="recovery-code">{recoveryCode}</p>
          </div>
          <Button onClick={() => navigate("/dashboard")} className="w-full text-black h-10 gap-2 font-semibold" style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)' }} data-testid="continue-btn">
            Weiter zur App <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Purchase flow: Show activation code after email lookup
  if (mode === "purchased" && purchaseStep === "code") {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-start sm:items-center justify-center px-4 pt-8 sm:pt-0 pb-40 sm:pb-10">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center">
            <PartyPopper className="w-10 h-10 mx-auto mb-3" style={{ color: '#C8FF4D' }} />
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Danke fuer deinen Kauf!</h1>
            <p className="text-sm text-zinc-400">
              {purchasePlan === "yearly" ? "Jahresplan" : "Monatsplan"} fuer <span className="text-white">{email}</span>
            </p>
          </div>

          <div className="p-5 rounded-xl bg-zinc-900 border border-emerald-500/30 text-center">
            <p className="text-xs text-zinc-400 mb-2">Dein Aktivierungscode (nur einmal gueltig!):</p>
            <p className="text-2xl font-mono font-bold text-emerald-400 tracking-widest" data-testid="activation-code-display">{activationCode}</p>
            <button
              onClick={() => { navigator.clipboard.writeText(activationCode); toast.success("Code kopiert!"); }}
              className="mt-2 text-xs text-cyan-400 hover:text-cyan-300"
            >Kopieren</button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs text-zinc-400 mb-1.5 block">Jetzt Passwort festlegen</Label>
              <div className="relative">
                <Input type={showPass ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 Zeichen" className="bg-zinc-900 border-zinc-800 text-white pr-10" required data-testid="input-password" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full text-black font-semibold h-10 gap-2" style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)' }} data-testid="auth-submit-btn">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Account aktivieren
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex items-start sm:items-center justify-center px-4 pt-10 sm:pt-0 pb-40 sm:pb-10">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-zinc-800 bg-zinc-900/80 mb-4">
            <Zap className="w-3 h-3" style={{ color: '#00D4FF' }} />
            <span className="text-[10px] font-medium tracking-wider uppercase text-zinc-400">Social IQ</span>
          </div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>
            {mode === "login" ? "Willkommen zurueck" : mode === "purchased" ? "Kauf abschliessen" : mode === "activate" ? "Account aktivieren" : "Passwort zuruecksetzen"}
          </h1>
          {mode === "purchased" && (
            <p className="text-sm text-zinc-400 mt-2">Gib die E-Mail-Adresse ein, mit der du gekauft hast.</p>
          )}
          {mode === "activate" && (
            <p className="text-sm text-zinc-400 mt-2">Gib deine E-Mail und den Aktivierungscode ein.</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-xs text-zinc-400 mb-1.5 block">E-Mail</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@email.de" className="bg-zinc-900 border-zinc-800 text-white" required data-testid="input-email" />
          </div>

          {mode === "activate" && (
            <div>
              <Label className="text-xs text-zinc-400 mb-1.5 block">Aktivierungscode</Label>
              <Input value={activationCode} onChange={e => setActivationCode(e.target.value)} placeholder="z.B. A3F2B1C9" className="bg-zinc-900 border-zinc-800 text-white font-mono uppercase" required data-testid="input-activation-code" />
            </div>
          )}

          {mode === "reset" ? (
            <>
              <div>
                <Label className="text-xs text-zinc-400 mb-1.5 block">Recovery-Code</Label>
                <Input value={resetCode} onChange={e => setResetCode(e.target.value)} placeholder="z.B. A3F2B1C9" className="bg-zinc-900 border-zinc-800 text-white font-mono" required data-testid="input-reset-code" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400 mb-1.5 block">Neues Passwort</Label>
                <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min. 6 Zeichen" className="bg-zinc-900 border-zinc-800 text-white" required data-testid="input-new-password" />
              </div>
            </>
          ) : mode !== "purchased" ? (
            <div>
              <Label className="text-xs text-zinc-400 mb-1.5 block">Passwort{mode === "activate" ? " festlegen" : ""}</Label>
              <div className="relative">
                <Input type={showPass ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 Zeichen" className="bg-zinc-900 border-zinc-800 text-white pr-10" required data-testid="input-password" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                  {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ) : null}

          {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}

          <Button type="submit" disabled={loading} className="w-full text-black font-semibold h-10 gap-2" style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #C8FF4D 100%)' }} data-testid="auth-submit-btn">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === "login" ? "Einloggen" : mode === "purchased" ? "Weiter" : mode === "activate" ? "Account aktivieren" : "Passwort zuruecksetzen"}
          </Button>
        </form>

        <div className="text-center space-y-2">
          {mode === "login" && (
            <>
              <button onClick={() => setMode("purchased")} className="text-sm text-zinc-400 hover:text-white transition-colors block w-full" data-testid="switch-activate">
                <ShieldCheck className="w-3.5 h-3.5 inline mr-1" />
                Gekauft? <span style={{ color: '#00D4FF' }}>Account aktivieren</span>
              </button>
              <a href="https://erfolg-beginnt-in-dir.com/social-iq" target="_blank" rel="noreferrer" className="text-sm text-zinc-400 hover:text-white transition-colors block w-full" data-testid="switch-buy">
                Noch kein Account? <span style={{ color: '#C8FF4D' }}>Jetzt kaufen</span>
              </a>
              <button onClick={() => setMode("reset")} className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors" data-testid="switch-reset">Passwort vergessen?</button>
            </>
          )}
          {(mode === "purchased" || mode === "activate") && (
            <button onClick={() => setMode("login")} className="text-sm text-zinc-400 hover:text-white transition-colors" data-testid="switch-login-from-activate">Zurueck zum <span style={{ color: '#00D4FF' }}>Login</span></button>
          )}
          {mode === "reset" && (
            <button onClick={() => setMode("login")} className="text-sm text-zinc-400 hover:text-white transition-colors" data-testid="switch-login-from-reset">Zurueck zum <span style={{ color: '#00D4FF' }}>Login</span></button>
          )}
        </div>
      </div>
    </div>
  );
}
